import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import classes from "./Management.module.scss";
import styles from "./dealer/DealerPipeline.module.scss";
import {
  getDealerAdminSummary,
  getDealerAdminInquiries,
  getDealerAdminFavorites,
  getDealerAdminPurchases,
  createDealerAdminPurchase,
  lookupDealerVehicle,
  lookupAuctionCar,
  DEALER_PURCHASE_STATUSES,
  INQUIRY_STATUSES,
  INQUIRY_STATUS_LABELS,
  INQUIRY_TYPE_LABELS,
  formatUsd,
  leadCars,
} from "../../services/dealerGarage";
import PipelineBoard from "./dealer/PipelineBoard";
import PurchasePanel, { PaymentChip } from "./dealer/PurchasePanel";
import InspectionPanel from "./dealer/InspectionPanel";
import InquiryPanel from "./dealer/InquiryPanel";
import CustomerPanel from "./dealer/CustomerPanel";
import CustomersSection from "./dealer/CustomersSection";
import CustomerLink from "./dealer/CustomerLink";
import InspectionsSection from "./dealer/InspectionsSection";
import BillingSection from "./dealer/BillingSection";
import QuoteFields from "./dealer/QuoteFields";
import useCustomerSearch from "./dealer/useCustomerSearch";
import { formatDate, formatPhone, vehicleLabel, customerLabel, customerIdOf, customerRefOf, daysInStep, parseMoney, needsCall } from "./dealer/dealerAdmin";

const SECTIONS = [
  { id: "pipeline", label: "Pipeline" },
  { id: "customers", label: "Customers" },
  { id: "inquiries", label: "Inquiries" },
  { id: "inspections", label: "Inspections" },
  { id: "purchases", label: "Purchases" },
  { id: "billing", label: "Billing" },
  { id: "favorites", label: "Saved Cars" },
];

// We don't buy a dealer car until the customer has paid, so its sale starts
// by waiting on the payment (the payment that clears the car invoice confirms
// the order). An auction win is already bought.
const startingStepFor = (kind) => (kind === "auction" ? "Order Confirmed" : "Awaiting Payment");

const emptyPurchaseForm = {
  // 'dealer': a dealer-stock / current-inventory listing; 'auction': a car we
  // won at auction for the customer.
  kind: "dealer",
  userId: "",
  vehicleQuery: "",
  status: startingStepFor("dealer"),
  price: "",
  purchaseDate: "",
  vin: "",
  notes: "",
  adminNotes: "",
  inquiryId: "",
  inspectionId: "",
  // What the customer was quoted on - it prices the invoice. Filled from the
  // lead when there is one (quoteFromLead, so the form can say so).
  quote: { quoteType: "", port: "", includeTitle: false },
  quoteFromLead: false,
  notify: true,
};

// The lead's quote, as the sale form's quote.
const quoteFrom = (quote) => ({
  quoteType: (quote && quote.quoteType) || "",
  port: (quote && quote.port) || "",
  includeTitle: !!(quote && quote.includeTitle),
});

// The Inquiries list's filter: a status shows its live leads, "archived" the
// dead ones, "all" both.
const inquiryQuery = (filter) => {
  if (filter === "all") return {};
  if (filter === "archived") return { archived: true };
  return { status: filter, archived: false };
};

// A car already in the US has no shipping or import to price.
const isInUs = (resolved) => !!resolved && (resolved.source || (resolved.snapshot && resolved.snapshot.source)) === "current-inventory";

/**
 * Staff view of everything customers do with dealer-stock cars: the pipeline
 * board, the leads (inquiries, quote and inspection requests from both sites),
 * inspections, the sales recorded against them with each car's step, billing,
 * and who has saved which car.
 */
const DealerStockManagement = () => {
  const [section, setSection] = useState("pipeline");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Inquiries
  const [inquiries, setInquiries] = useState([]);
  const [inquiryFilter, setInquiryFilter] = useState("new");

  // Purchases
  const [purchases, setPurchases] = useState([]);
  const [purchaseForm, setPurchaseForm] = useState({ ...emptyPurchaseForm });
  const [userQuery, setUserQuery] = useState("");
  const [showUserList, setShowUserList] = useState(false);
  const [resolvedVehicle, setResolvedVehicle] = useState(null);
  const [lookupState, setLookupState] = useState("");
  const [saving, setSaving] = useState(false);

  // Panels open over any section; refreshKey reloads the board and lists
  // after a panel changes something.
  const [openPurchaseId, setOpenPurchaseId] = useState("");
  // The tab a purchase opens on: Billing for a sale just started, else its own default.
  const [openPurchaseTab, setOpenPurchaseTab] = useState("");
  const [openInspectionId, setOpenInspectionId] = useState("");
  // ?lead=<inquiryId> (from Dealer Analytics) opens that lead on arrival.
  const [openInquiryId, setOpenInquiryId] = useState(() => {
    const lead = new URLSearchParams(window.location.search).get("lead");
    return /^[0-9a-f]{24}$/i.test(lead || "") ? lead : "";
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  // The customer panel ({ userId } | { inquiryId } | { inspectionId }). It
  // sits under the record panels, so a lead or purchase opened from it comes
  // back to it. ?customer=<userId> / ?customerLead=<inquiryId> open it from a
  // link (the Users tab, emails).
  const [searchParams, setSearchParams] = useSearchParams();
  const [openCustomer, setOpenCustomer] = useState(() => {
    const userId = searchParams.get("customer");
    const inquiryId = searchParams.get("customerLead");
    if (/^[0-9a-f]{24}$/i.test(userId || "")) return { userId };
    if (/^[0-9a-f]{24}$/i.test(inquiryId || "")) return { inquiryId };
    return null;
  });
  const closeLead = () => {
    setOpenInquiryId("");
    if (searchParams.has("lead")) {
      const next = new URLSearchParams(searchParams);
      next.delete("lead");
      setSearchParams(next, { replace: true });
    }
    refresh();
  };
  const closeCustomer = () => {
    setOpenCustomer(null);
    if (searchParams.has("customer") || searchParams.has("customerLead")) {
      const next = new URLSearchParams(searchParams);
      next.delete("customer");
      next.delete("customerLead");
      setSearchParams(next, { replace: true });
    }
    refresh();
  };
  // From a record panel's header: that panel gives way to the customer's.
  const openCustomerFrom = (ref) => {
    if (!ref) return;
    setOpenInquiryId("");
    setOpenInspectionId("");
    setOpenPurchaseId("");
    setOpenPurchaseTab("");
    setOpenCustomer(ref);
  };

  // Favourites
  const [favorites, setFavorites] = useState([]);
  const [favoriteFilter, setFavoriteFilter] = useState("");

  const loadSummary = useCallback(() => {
    getDealerAdminSummary().then((res) => {
      if (res?.message === "Success") setSummary(res.data);
    }).catch(() => {});
  }, []);

  const loadInquiries = useCallback(() => {
    setLoading(true);
    getDealerAdminInquiries({ ...inquiryQuery(inquiryFilter), limit: 200 })
      .then((res) => setInquiries(res?.message === "Success" ? res.data : []))
      .catch(() => setInquiries([]))
      .finally(() => setLoading(false));
  }, [inquiryFilter]);

  const loadPurchases = useCallback(() => {
    setLoading(true);
    getDealerAdminPurchases({ limit: 300 })
      .then((res) => setPurchases(res?.message === "Success" ? res.data : []))
      .catch(() => setPurchases([]))
      .finally(() => setLoading(false));
  }, []);

  const loadFavorites = useCallback(() => {
    setLoading(true);
    getDealerAdminFavorites({ limit: 500 })
      .then((res) => setFavorites(res?.message === "Success" ? res.data : []))
      .catch(() => setFavorites([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary, refreshKey]);

  useEffect(() => {
    if (section === "inquiries") loadInquiries();
    if (section === "purchases") loadPurchases();
    if (section === "favorites") loadFavorites();
  }, [section, loadInquiries, loadPurchases, loadFavorites, refreshKey]);

  // ---- Inquiries ----

  const startPurchaseFromInquiry = (inquiry) => {
    // Their account, or the one found (or guest account opened) under the lead's email.
    const userId = (inquiry.userId && typeof inquiry.userId === "object" ? inquiry.userId._id : inquiry.userId) || inquiry.accountUserId;
    // The list comes with the customer's account filled in.
    const u = inquiry.userId && typeof inquiry.userId === "object" ? inquiry.userId : null;
    setPurchaseForm({
      ...emptyPurchaseForm,
      userId: userId || "",
      vehicleQuery: inquiry.vehicleId || "",
      inquiryId: inquiry._id,
      price: inquiry.quote && inquiry.quote.estimatedTotal ? String(Math.round(inquiry.quote.estimatedTotal)) : "",
      quote: quoteFrom(inquiry.quote),
      quoteFromLead: !!(inquiry.quote && inquiry.quote.quoteType),
    });
    setUserQuery(u ? `${u.firstName} ${u.lastName} (${u.email})` : (inquiry.email || ""));
    setResolvedVehicle(inquiry.vehicle ? { vehicleId: inquiry.vehicleId, snapshot: inquiry.vehicle } : null);
    setLookupState(inquiry.vehicle ? "ok" : "");
    setOpenInquiryId("");
    // The sale form is on the page, under the customer panel if one is open.
    setOpenCustomer(null);
    setSection("purchases");
  };

  // From an inspection the customer is buying. Its lead (the conversation the
  // inspection panel loaded, when there is one) supplies the quote and price -
  // on a lead with several cars, the quote asked for on the inspected one.
  const startPurchaseFromInspection = (inspection, inquiry) => {
    const userId = customerIdOf(inspection) || inspection.accountUserId || "";
    const u = inspection.userId && typeof inspection.userId === "object" ? inspection.userId : null;
    const car = leadCars(inquiry).find((c) => c.vehicleId === inspection.vehicleId);
    const quote = (car || inquiry || {}).quote;
    setPurchaseForm({
      ...emptyPurchaseForm,
      userId,
      vehicleQuery: inspection.vehicleId || "",
      inspectionId: inspection._id,
      price: quote && quote.estimatedTotal ? String(Math.round(quote.estimatedTotal)) : "",
      quote: quoteFrom(quote),
      quoteFromLead: !!(quote && quote.quoteType),
    });
    setUserQuery(u ? `${u.firstName} ${u.lastName} (${u.email})` : (inspection.email || ""));
    setResolvedVehicle(inspection.vehicle ? { vehicleId: inspection.vehicleId, snapshot: inspection.vehicle } : null);
    setLookupState(inspection.vehicle ? "ok" : "");
    setOpenInspectionId("");
    setOpenCustomer(null);
    setSection("purchases");
  };

  // A lead's "Book an inspection" opens the inspection over the same screen.
  const openInspectionFromInquiry = (id) => {
    setOpenInquiryId("");
    setOpenInspectionId(id);
    refresh();
  };

  // ---- Purchases ----

  const filteredUsers = useCustomerSearch(userQuery, showUserList);

  const pickUser = (u) => {
    setPurchaseForm((f) => ({ ...f, userId: u._id }));
    setUserQuery(`${u.firstName} ${u.lastName} (${u.email})`);
    setShowUserList(false);
  };

  const lookupVehicle = () => {
    const q = purchaseForm.vehicleQuery.trim();
    if (!q) return;
    setLookupState("loading");
    (purchaseForm.kind === "auction" ? lookupAuctionCar(q) : lookupDealerVehicle(q)).then((res) => {
      if (res?.message === "Success") {
        setResolvedVehicle(res.data);
        setPurchaseForm((f) => ({ ...f, vehicleQuery: res.data.vehicleId }));
        setLookupState("ok");
      } else {
        setResolvedVehicle(null);
        setLookupState("notfound");
      }
    }).catch(() => {
      setResolvedVehicle(null);
      setLookupState("notfound");
    });
  };

  const submitPurchase = (e) => {
    e.preventDefault();
    if (!purchaseForm.userId) {
      alert("Pick the customer first.");
      return;
    }
    const idMatch = purchaseForm.vehicleQuery.match(/[0-9a-fA-F]{24}/);
    if (!idMatch) {
      alert("Enter the vehicle's listing ID or paste its URL.");
      return;
    }
    const price = parseMoney(purchaseForm.price);
    if (Number.isNaN(price)) {
      alert("Price must be a number.");
      return;
    }
    const auction = purchaseForm.kind === "auction";
    if (auction && !price) {
      alert("Enter the winning price in dollars - it prices the invoice.");
      return;
    }
    const inUs = isInUs(resolvedVehicle);
    if (!inUs && !purchaseForm.quote.quoteType) {
      alert("Pick shipped or imported - it prices the invoice.");
      return;
    }
    setSaving(true);
    createDealerAdminPurchase({
      userId: purchaseForm.userId,
      vehicleId: idMatch[0],
      ...(auction ? { source: "auction", winningBid: price } : {}),
      quote: {
        quoteType: inUs ? null : purchaseForm.quote.quoteType,
        port: inUs ? "" : purchaseForm.quote.port,
        includeTitle: purchaseForm.quote.includeTitle,
      },
      status: purchaseForm.status,
      price,
      purchaseDate: purchaseForm.purchaseDate || "",
      vin: purchaseForm.vin.trim(),
      notes: purchaseForm.notes.trim(),
      adminNotes: purchaseForm.adminNotes.trim(),
      inquiryId: purchaseForm.inquiryId || null,
      inspectionId: purchaseForm.inspectionId || null,
      notify: purchaseForm.notify,
    }).then((res) => {
      if (res?.message === "Purchase created successfully") {
        setPurchaseForm({ ...emptyPurchaseForm });
        setUserQuery("");
        setResolvedVehicle(null);
        setLookupState("");
        refresh();
        // Straight into the new purchase's billing: the next thing staff do is invoice it.
        setOpenPurchaseTab("billing");
        setOpenPurchaseId(res.data._id);
      } else {
        alert(res?.message || "Could not create the purchase.");
      }
    }).catch(() => alert("Could not create the purchase.")).finally(() => setSaving(false));
  };

  // ---- Favourites ----

  const favoritesByVehicle = useMemo(() => {
    const q = favoriteFilter.trim().toLowerCase();
    const rows = q
      ? favorites.filter((f) => `${vehicleLabel(f)} ${customerLabel(f)} ${f.vehicleId}`.toLowerCase().includes(q))
      : favorites;
    const counts = {};
    rows.forEach((f) => { counts[f.vehicleId] = (counts[f.vehicleId] || 0) + 1; });
    return { rows, counts };
  }, [favorites, favoriteFilter]);

  return (
    <div>
      <h1>Dealer Stock</h1>

      {summary && (
        <div className={classes.statsGrid}>
          <div className={classes.statCard}>
            <div className={classes.statValue}>{summary.newInquiries}</div>
            <div className={classes.statLabel}>New inquiries</div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statValue}>{summary.contactedInquiries || 0}</div>
            <div className={classes.statLabel}>In conversation</div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statValue} style={summary.waitingOnUs && summary.waitingOnUs.overdue ? { color: "#b42318" } : undefined}>
              {(summary.waitingOnUs && summary.waitingOnUs.total) || 0}
            </div>
            <div className={classes.statLabel}>
              Waiting on our reply{summary.waitingOnUs && summary.waitingOnUs.overdue ? ` · ${summary.waitingOnUs.overdue} over 2 days` : ""}
            </div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statValue}>{summary.searchingInquiries || 0}</div>
            <div className={classes.statLabel}>Searching for a car</div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statValue}>
              {/* Requests still waiting on a deposit are on the board as leads. */}
              {["Requested", "Accepted", "Scheduled"].reduce((n, st) => n + ((summary.inspections && summary.inspections[st]) || 0), 0)
                - ((summary.inspections && summary.inspections.requestsAsLeads) || 0)}
            </div>
            <div className={classes.statLabel}>
              Open inspections{summary.inspections && summary.inspections.awaitingDecision ? ` · ${summary.inspections.awaitingDecision} awaiting decision` : ""}
            </div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statValue}>{summary.openPurchases}</div>
            <div className={classes.statLabel}>Cars in progress</div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statValue}>{formatUsd(summary.outstanding || 0, { cents: false })}</div>
            <div className={classes.statLabel}>Outstanding on {summary.unpaidInvoices || 0} invoice{summary.unpaidInvoices === 1 ? "" : "s"}</div>
          </div>
        </div>
      )}

      <div className={classes.analyticsTabs}>
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`${classes.analyticsTab} ${section === s.id ? classes.analyticsTabActive : ""}`}
            onClick={() => setSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* ------------------------------------------------------------ */}
      {section === "pipeline" && (
        <PipelineBoard
          refreshKey={refreshKey}
          onRefresh={refresh}
          onChanged={() => loadSummary()}
          onOpenInquiry={(inquiry) => setOpenInquiryId(inquiry._id)}
          onOpenInspection={setOpenInspectionId}
          onOpenPurchase={setOpenPurchaseId}
        />
      )}

      {section === "customers" && <CustomersSection onOpen={setOpenCustomer} />}

      {section === "inspections" && <InspectionsSection refreshKey={refreshKey} onOpen={setOpenInspectionId} onOpenCustomer={setOpenCustomer} />}

      {section === "billing" && (
        <BillingSection onOpenPurchase={setOpenPurchaseId} onOpenCustomer={setOpenCustomer} />
      )}

      {/* ------------------------------------------------------------ */}
      {section === "inquiries" && (
        <div>
          <div className={classes.formButtons} style={{ alignItems: "center" }}>
            <label className={classes.formLabel} htmlFor="dsm-inq-filter">Show</label>
            <select
              id="dsm-inq-filter"
              className={classes.formInput}
              style={{ width: "auto", marginTop: 0 }}
              value={inquiryFilter}
              onChange={(e) => setInquiryFilter(e.target.value)}
            >
              {INQUIRY_STATUSES.map((st) => <option key={st} value={st}>{INQUIRY_STATUS_LABELS[st] || st}</option>)}
              <option value="archived">Archived</option>
              <option value="all">All</option>
            </select>
            <button type="button" className={classes.tableButton} onClick={loadInquiries}>Refresh</button>
          </div>

          <div className={`${classes.tableContainer} ${styles.scrollTable}`}>
            <table className={classes.table}>
              <thead>
                <tr className={classes.tableHeaderRow}>
                  <th className={classes.tableHead}>Date</th>
                  <th className={classes.tableHead}>Type</th>
                  <th className={classes.tableHead}>Customer</th>
                  <th className={classes.tableHead}>Vehicle</th>
                  <th className={classes.tableHead}>From</th>
                  <th className={classes.tableHead}>Status</th>
                  <th className={classes.tableHead}></th>
                </tr>
              </thead>
              <tbody>
                {loading && inquiries.length === 0 && (
                  <tr className={classes.tableRow}><td className={classes.tableData} colSpan={7}>Loading...</td></tr>
                )}
                {!loading && inquiries.length === 0 && (
                  <tr className={classes.tableRow}><td className={classes.tableData} colSpan={7}>No inquiries.</td></tr>
                )}
                {inquiries.map((inquiry) => (
                  <tr key={inquiry._id} className={classes.tableRow}>
                    <td className={classes.tableData}>{formatDate(inquiry.createdAt)}</td>
                    <td className={classes.tableData}>{INQUIRY_TYPE_LABELS[inquiry.type] || inquiry.type}</td>
                    <td className={classes.tableData}>
                      <CustomerLink customer={customerRefOf(inquiry, "inquiry")} onOpen={setOpenCustomer}>{customerLabel(inquiry)}</CustomerLink>
                      {inquiry.phone ? <><br /><small>{formatPhone(inquiry.phone)}</small></> : null}
                    </td>
                    <td className={classes.tableData}>
                      {inquiry.vehicle && inquiry.vehicle.url ? (
                        <a href={inquiry.vehicle.url} target="_blank" rel="noopener noreferrer" className={classes.clickableCell}>
                          {vehicleLabel(inquiry)}
                        </a>
                      ) : vehicleLabel(inquiry)}
                      {(inquiry.extraCars || []).length > 0 && (
                        <><br /><small title={inquiry.extraCars.map(vehicleLabel).join("\n")}>+{inquiry.extraCars.length} more {inquiry.extraCars.length === 1 ? "car" : "cars"}</small></>
                      )}
                    </td>
                    <td className={classes.tableData}>{inquiry.origin === "portal" ? "Portal" : "Website"}</td>
                    <td className={classes.tableData}>
                      {INQUIRY_STATUS_LABELS[inquiry.status] || inquiry.status}
                      {inquiry.archivedAt && <><br /><small>Archived {formatDate(inquiry.archivedAt)}</small></>}
                      {needsCall(inquiry) && <><br /><small>On the call list</small></>}
                      {inquiry.staffUnread > 0 && (
                        <span className={classes.unreadBadge}>{inquiry.staffUnread} new</span>
                      )}
                    </td>
                    <td className={classes.tableData}>
                      <button type="button" className={classes.tableButton} onClick={() => setOpenInquiryId(inquiry._id)}>
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------ */}
      {section === "purchases" && (
        <div>
          <h3 className={classes.headingH3}>Record a sale</h3>
          <div className={styles.segmented} role="group" aria-label="What was sold">
            {[{ id: "dealer", label: "Dealer stock" }, { id: "auction", label: "Auction win" }].map((k) => (
              <button
                key={k.id}
                type="button"
                className={purchaseForm.kind === k.id ? styles.segmentedActive : undefined}
                aria-pressed={purchaseForm.kind === k.id}
                onClick={() => {
                  setPurchaseForm((f) => ({
                    ...f,
                    kind: k.id,
                    status: startingStepFor(k.id),
                    vehicleQuery: "",
                    inquiryId: k.id === "auction" ? "" : f.inquiryId,
                    inspectionId: k.id === "auction" ? "" : f.inspectionId,
                    // Auction wins are usually brought in fully imported.
                    quote: k.id === "auction" && !f.quote.quoteType ? { ...f.quote, quoteType: "imported" } : f.quote,
                  }));
                  setResolvedVehicle(null);
                  setLookupState("");
                }}
              >
                {k.label}
              </button>
            ))}
          </div>
          <form className={classes.modalForm} onSubmit={submitPurchase} autoComplete="off">
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Customer</label>
              <div className={classes.wfull}>
                <input
                  className={classes.formInput}
                  placeholder="Search by name or email"
                  value={userQuery}
                  onChange={(e) => { setUserQuery(e.target.value); setPurchaseForm((f) => ({ ...f, userId: "" })); setShowUserList(true); }}
                  onFocus={() => setShowUserList(true)}
                  onBlur={() => setTimeout(() => setShowUserList(false), 150)}
                />
                {showUserList && filteredUsers.length > 0 && (
                  <div className={classes.usersList}>
                    {filteredUsers.map((u) => (
                      <div key={u._id} className={classes.nameList} onMouseDown={() => pickUser(u)}>
                        {u.firstName} {u.lastName} <small>({u.email}){u.isGuest ? " · guest account" : ""}</small>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!purchaseForm.userId && purchaseForm.inquiryId && (
                <small>This inquiry came from a guest with no account. Ask them to register, then pick them here.</small>
              )}
            </div>

            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Vehicle</label>
              <div className={classes.formButtons} style={{ width: "100%" }}>
                <input
                  className={classes.formInput}
                  placeholder={purchaseForm.kind === "auction" ? "Auction car page link or ID" : "Listing ID or importjdmmagic.com URL"}
                  value={purchaseForm.vehicleQuery}
                  onChange={(e) => { setPurchaseForm((f) => ({ ...f, vehicleQuery: e.target.value })); setLookupState(""); setResolvedVehicle(null); }}
                />
                <button type="button" className={classes.tableButton} onClick={lookupVehicle} disabled={lookupState === "loading"}>
                  {lookupState === "loading" ? "Looking up..." : "Look up"}
                </button>
              </div>
              {lookupState === "ok" && resolvedVehicle && (
                <small>
                  Found: <strong>{vehicleLabel({ vehicle: resolvedVehicle.snapshot, vehicleId: resolvedVehicle.vehicleId })}</strong>
                  {resolvedVehicle.snapshot && resolvedVehicle.snapshot.status ? ` (${resolvedVehicle.snapshot.status})` : ""}
                  {resolvedVehicle.source === "current-inventory" ? " - US inventory" : resolvedVehicle.source === "auction" ? " - auction car" : " - Japan dealer stock"}
                </small>
              )}
              {lookupState === "ok" && resolvedVehicle && resolvedVehicle.existingPurchase && (
                <small style={{ color: "#B42318" }}>This car is already in the pipeline - open it from the board instead.</small>
              )}
              {lookupState === "notfound" && <small style={{ color: "#B42318" }}>No {purchaseForm.kind === "auction" ? "auction car" : "listing"} found for that ID.</small>}
            </div>
            <div className={styles.grid2}>
              <QuoteFields
                quote={purchaseForm.quote}
                inUs={isInUs(resolvedVehicle)}
                onChange={(quote) => setPurchaseForm((f) => ({ ...f, quote }))}
              />
            </div>
            {purchaseForm.quoteFromLead && (
              <small className={styles.hint}>Filled in from the lead's quote. Change it if the deal changed.</small>
            )}

            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Starting step</label>
              <select className={classes.formInput} value={purchaseForm.status} onChange={(e) => setPurchaseForm((f) => ({ ...f, status: e.target.value }))}>
                {DEALER_PURCHASE_STATUSES.filter((st) => st !== "Cancelled").map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
              {purchaseForm.status === "Awaiting Payment" && (
                <small>Nothing is bought yet. Invoice them next: the customer hears from you with the invoice, and the payment that clears it moves the sale to Order Confirmed and emails their order confirmation.</small>
              )}
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>{purchaseForm.kind === "auction" ? "Winning price (USD)" : "Agreed price (USD)"}</label>
              <input className={classes.formInput} value={purchaseForm.price} onChange={(e) => setPurchaseForm((f) => ({ ...f, price: e.target.value }))} placeholder={purchaseForm.kind === "auction" ? "The car's hammer price in dollars, e.g. 18100" : "e.g. 24500"} />
              {purchaseForm.kind !== "auction" && (
                <small>What they pay for the car as quoted (shipped, or all-in if imported), before the inspection fee. The invoice adds up to it.</small>
              )}
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Purchase date</label>
              <input className={classes.formInput} type="date" value={purchaseForm.purchaseDate} onChange={(e) => setPurchaseForm((f) => ({ ...f, purchaseDate: e.target.value }))} />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>VIN / chassis no.</label>
              <input className={classes.formInput} value={purchaseForm.vin} onChange={(e) => setPurchaseForm((f) => ({ ...f, vin: e.target.value }))} />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Note to customer</label>
              <textarea className={classes.formInput} rows={2} value={purchaseForm.notes} onChange={(e) => setPurchaseForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Staff notes</label>
              <textarea className={classes.formInput} rows={2} value={purchaseForm.adminNotes} onChange={(e) => setPurchaseForm((f) => ({ ...f, adminNotes: e.target.value }))} />
            </div>
            {purchaseForm.status !== "Awaiting Payment" && (
              <label className={styles.checkboxRow}>
                <input type="checkbox" checked={purchaseForm.notify} onChange={(e) => setPurchaseForm((f) => ({ ...f, notify: e.target.checked }))} />
                Email the customer their order confirmation
              </label>
            )}
            <div className={classes.formButtons}>
              <button type="submit" className={`${classes.tableButton} ${classes.tableSuccessButton}`} disabled={saving}>
                {saving ? "Saving..." : purchaseForm.status === "Awaiting Payment" ? "Start the sale" : "Save purchase"}
              </button>
              <button type="button" className={classes.tableButton} onClick={() => { setPurchaseForm({ ...emptyPurchaseForm, kind: purchaseForm.kind, status: startingStepFor(purchaseForm.kind) }); setUserQuery(""); setResolvedVehicle(null); setLookupState(""); }}>
                Clear
              </button>
            </div>
          </form>

          <h3 className={classes.headingH3} style={{ marginTop: 24 }}>All purchases</h3>
          <div className={`${classes.tableContainer} ${styles.scrollTable}`}>
            <table className={classes.table}>
              <thead>
                <tr className={classes.tableHeaderRow}>
                  <th className={classes.tableHead}>Date</th>
                  <th className={classes.tableHead}>Customer</th>
                  <th className={classes.tableHead}>Vehicle</th>
                  <th className={classes.tableHead}>Price</th>
                  <th className={classes.tableHead}>Step</th>
                  <th className={classes.tableHead}>Payment</th>
                  <th className={classes.tableHead}></th>
                </tr>
              </thead>
              <tbody>
                {loading && purchases.length === 0 && (
                  <tr className={classes.tableRow}><td className={classes.tableData} colSpan={7}>Loading...</td></tr>
                )}
                {!loading && purchases.length === 0 && (
                  <tr className={classes.tableRow}><td className={classes.tableData} colSpan={7}>No dealer-stock purchases recorded yet.</td></tr>
                )}
                {purchases.map((purchase) => (
                  <tr key={purchase._id} className={classes.tableRow}>
                    <td className={classes.tableData}>{formatDate(purchase.purchaseDate || purchase.createdAt)}</td>
                    <td className={classes.tableData}>
                      <CustomerLink customer={customerRefOf(purchase)} onOpen={setOpenCustomer}>{customerLabel(purchase)}</CustomerLink>
                    </td>
                    <td className={classes.tableData}>
                      {purchase.vehicle && purchase.vehicle.url ? (
                        <a href={purchase.vehicle.url} target="_blank" rel="noopener noreferrer" className={classes.clickableCell}>
                          {vehicleLabel(purchase)}
                        </a>
                      ) : vehicleLabel(purchase)}
                      {purchase.vin ? <><br /><small>VIN {purchase.vin}</small></> : null}
                    </td>
                    <td className={classes.tableData}>{purchase.price ? formatUsd(purchase.price, { cents: false }) : "-"}</td>
                    <td className={classes.tableData}>
                      {purchase.status}
                      {daysInStep(purchase) !== null && <><br /><small>{daysInStep(purchase)}d in this step</small></>}
                    </td>
                    <td className={classes.tableData}><PaymentChip billing={purchase.billing} /></td>
                    <td className={classes.tableData}>
                      <button type="button" className={classes.tableButton} onClick={() => setOpenPurchaseId(purchase._id)}>Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      )}

      {/* ------------------------------------------------------------ */}
      {section === "favorites" && (
        <div>
          <input
            className={classes.search_users}
            placeholder="Filter by car, customer or listing ID"
            value={favoriteFilter}
            onChange={(e) => setFavoriteFilter(e.target.value)}
          />
          <div className={`${classes.tableContainer} ${styles.scrollTable}`}>
            <table className={classes.table}>
              <thead>
                <tr className={classes.tableHeaderRow}>
                  <th className={classes.tableHead}>Saved</th>
                  <th className={classes.tableHead}>Customer</th>
                  <th className={classes.tableHead}>Vehicle</th>
                  <th className={classes.tableHead}>Saves on this car</th>
                </tr>
              </thead>
              <tbody>
                {loading && favorites.length === 0 && (
                  <tr className={classes.tableRow}><td className={classes.tableData} colSpan={4}>Loading...</td></tr>
                )}
                {!loading && favoritesByVehicle.rows.length === 0 && (
                  <tr className={classes.tableRow}><td className={classes.tableData} colSpan={4}>No saved cars yet.</td></tr>
                )}
                {favoritesByVehicle.rows.map((favorite) => (
                  <tr key={favorite._id} className={classes.tableRow}>
                    <td className={classes.tableData}>{formatDate(favorite.createdAt)}</td>
                    <td className={classes.tableData}>
                      <CustomerLink customer={customerRefOf(favorite)} onOpen={setOpenCustomer}>{customerLabel(favorite)}</CustomerLink>
                      {favorite.userId && favorite.userId.phone ? <><br /><small>{formatPhone(favorite.userId.phone)}</small></> : null}
                    </td>
                    <td className={classes.tableData}>
                      {favorite.vehicle && favorite.vehicle.url ? (
                        <a href={favorite.vehicle.url} target="_blank" rel="noopener noreferrer" className={classes.clickableCell}>
                          {vehicleLabel(favorite)}
                        </a>
                      ) : vehicleLabel(favorite)}
                      {favorite.vehicle && favorite.vehicle.status && !["active", "available"].includes(favorite.vehicle.status)
                        ? <><br /><small>({favorite.vehicle.status})</small></> : null}
                    </td>
                    <td className={classes.tableData}>{favoritesByVehicle.counts[favorite.vehicleId] || 1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Under the record panels: what's opened from it stacks on top. */}
      {openCustomer && (
        <CustomerPanel
          key={JSON.stringify(openCustomer)}
          customerRef={openCustomer}
          refreshKey={refreshKey}
          onClose={closeCustomer}
          onOpenInquiry={setOpenInquiryId}
          onOpenInspection={setOpenInspectionId}
          onOpenPurchase={setOpenPurchaseId}
        />
      )}

      {openPurchaseId && (
        <PurchasePanel
          key={openPurchaseId}
          purchaseId={openPurchaseId}
          initialTab={openPurchaseTab}
          onClose={() => { setOpenPurchaseId(""); setOpenPurchaseTab(""); refresh(); }}
          onChanged={() => loadSummary()}
          onOpenCustomer={openCustomerFrom}
        />
      )}

      {openInquiryId && (
        <InquiryPanel
          key={openInquiryId}
          inquiryId={openInquiryId}
          onClose={closeLead}
          onChanged={() => loadSummary()}
          onOpenInspection={openInspectionFromInquiry}
          onRecordPurchase={startPurchaseFromInquiry}
          onOpenCustomer={openCustomerFrom}
          onOpenLead={(id) => { setOpenInquiryId(String(id)); refresh(); }}
        />
      )}

      {openInspectionId && (
        <InspectionPanel
          key={openInspectionId}
          inspectionId={openInspectionId}
          onClose={() => { setOpenInspectionId(""); refresh(); }}
          onChanged={() => loadSummary()}
          onRecordPurchase={startPurchaseFromInspection}
          onOpenCustomer={openCustomerFrom}
        />
      )}
    </div>
  );
};

export default DealerStockManagement;
